if (!customElements.get('variant-selects')) {
  class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.lastGoodSelection = new Map();
      }

      // R9-1 helper: only the values that are genuinely not purchasable carry aria-disabled.
      static isBlocked(el) {
        return el?.getAttribute?.('aria-disabled') === 'true';
      }

      rememberSelection() {
        // This snapshot represents the last server-confirmed selection. A user change is not
        // committed here; the successful section response replaces this custom element and the
        // replacement records the new confirmed state in connectedCallback().
        this.lastGoodSelection.clear();
        this.querySelectorAll('fieldset').forEach((fs, i) => {
          const checked = fs.querySelector('input:checked');
          if (checked) this.lastGoodSelection.set(`fieldset:${i}`, checked.id || checked.value);
        });
        this.querySelectorAll('select').forEach((sel, i) => {
          this.lastGoodSelection.set(`select:${i}`, sel.value);
        });
      }

      restoreSelection() {
        this.querySelectorAll('fieldset').forEach((fs, i) => {
          const want = this.lastGoodSelection.get(`fieldset:${i}`);
          if (want === undefined) return;
          const el = [...fs.querySelectorAll('input')].find((n) => (n.id || n.value) === want);
          if (el) el.checked = true;
        });
        this.querySelectorAll('select').forEach((sel, i) => {
          const want = this.lastGoodSelection.get(`select:${i}`);
          if (want === undefined || sel.value === want) return;
          sel.value = want;
          // selectedOptionValues reads the ATTRIBUTE, so the property alone is not enough.
          [...sel.options].forEach((o) => o.removeAttribute('selected'));
          const target = [...sel.options].find((o) => o.value === want);
          if (target) target.setAttribute('selected', 'selected');
        });
      }

      // R9-1: normalise ?variant= to the server-rendered effective Variant.
      normalizeUrl() {
        // Quick View and featured-product render the picker with update_url:false, so they never
        // reach this branch and can never rewrite the background page URL.
        if (this.dataset.updateUrl === 'false') return;
        const effective = this.dataset.effectiveVariantId;
        if (!effective) return;
        // Must start from the CURRENT url: dataset.url is {{ product.url }} and carries neither
        // preview_theme_id/_ab/_fd/_sc nor campaign params. Starting from it would kick a theme
        // preview back to the live theme.
        const target = new URL(window.location.href);
        if (target.searchParams.get('variant') === effective) return;
        target.searchParams.set('variant', effective);
        window.history.replaceState({}, '', target.toString());
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getSelectedOptionValuesThrough(target) {
        const changedGroup = target.closest?.('.product-form__input');
        if (!changedGroup) return this.selectedOptionValues;

        const optionGroups = Array.from(this.querySelectorAll('.product-form__input')).filter(
          (group) => group.closest('variant-selects') === this
        );
        const changedPosition = optionGroups.indexOf(changedGroup);
        if (changedPosition < 0) return this.selectedOptionValues;

        // A preceding option change invalidates every selected value after it. For example,
        // changing Color must not submit the Size that belonged to the previous Color; Shopify
        // can then resolve the partial option prefix to that Color's first valid combination.
        return optionGroups
          .slice(0, changedPosition + 1)
          .map((group) => group.querySelector('select option[selected], fieldset input:checked')?.dataset.optionValueId)
          .filter(Boolean);
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.normalizeUrl();
        this.rememberSelection();

        // Capture phase: stop pointer and space/enter activation before the browser flips checked.
        this.addEventListener(
          'click',
          (event) => {
            const blocked = event.target.closest?.('[aria-disabled="true"]');
            if (blocked && this.contains(blocked)) {
              event.preventDefault();
              event.stopPropagation();
            }
          },
          true
        );
        this.addEventListener(
          'keydown',
          (event) => {
            if ((event.key === ' ' || event.key === 'Enter') && VariantSelects.isBlocked(event.target)) {
              event.preventDefault();
              event.stopPropagation();
            }
          },
          true
        );

        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          // Arrow keys move within a radiogroup and fire change directly, so click/keydown cannot
          // catch them. Roll the selection back instead of merely skipping the request.
          if (VariantSelects.isBlocked(target)) {
            this.restoreSelection();
            event.stopImmediatePropagation();
            return;
          }
          this.updateSelectedSwatchValue(event);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.getSelectedOptionValuesThrough(target),
            },
          });
        });
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            ?.removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
  }
  customElements.define('variant-selects', VariantSelects);
}
